import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, extractError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileText, CheckCircle2, XCircle, RefreshCw, FileSignature, Download } from "lucide-react";

const DOC_LABELS = {
  id_doc: "Pièce d'identité du représentant",
  rccm: "Registre du commerce (RCCM)",
  activity_proof: "Justificatif d'activité",
  address_proof: "Justificatif d'adresse",
  vehicles_docs: "Documents véhicules",
  insurance: "Attestation d'assurance",
  rib: "RIB pour reversements",
};
const ALL_DOC_TYPES = Object.keys(DOC_LABELS);

const STATUS_META = {
  not_submitted: { label: "Non fourni", cls: "bg-gray-100 text-gray-600" },
  submitted: { label: "Reçu", cls: "bg-blue-100 text-blue-700" },
  in_review: { label: "En vérification", cls: "bg-amber-100 text-amber-800" },
  validated: { label: "Validé", cls: "bg-[#dcfce7] text-[#0e7a3f]" },
  rejected: { label: "Refusé", cls: "bg-red-100 text-red-700" },
  replacement_needed: { label: "À remplacer", cls: "bg-orange-100 text-orange-700" },
};

const PS_LABELS = {
  pending: "En attente", in_review: "En vérification", docs_missing: "Documents manquants",
  contract_pending: "Contrat en attente", contract_signed: "Contrat accepté",
  validated: "Validée (active)", refused: "Refusée", deactivated: "Désactivée",
};

export default function AdminAgencyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [commission, setCommission] = useState("");
  const [required, setRequired] = useState([]);
  const [statusForm, setStatusForm] = useState({ partnership_status: "", note: "" });

  const load = () => api.get(`/agencies/${id}`).then((r) => {
    setAgency(r.data);
    setCommission(String(r.data.commission_percent ?? ""));
    setRequired(r.data.required_documents || []);
    setStatusForm({ partnership_status: r.data.partnership_status || "pending", note: "" });
  }).catch((e) => toast.error(extractError(e)));
  useEffect(() => { load(); }, [id]);

  const reviewDoc = async (docType, action) => {
    const note = action !== "validate" ? window.prompt(`Note pour ${DOC_LABELS[docType]} (motif) :`, "") : "";
    if (action !== "validate" && note === null) return;
    try {
      await api.post(`/agencies/${id}/documents/review`, { doc_type: docType, action, note: note || "" });
      toast.success("Document mis à jour");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const saveCommission = async () => {
    try {
      await api.put(`/agencies/${id}/commission`, { commission_percent: parseFloat(commission) || 0 });
      toast.success("Commission mise à jour");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const saveRequired = async () => {
    try {
      await api.put(`/agencies/${id}/required-docs`, { required_documents: required });
      toast.success("Documents requis mis à jour");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const transitionStatus = async () => {
    try {
      await api.post(`/agencies/${id}/partnership-status`, statusForm);
      toast.success(`Statut → ${PS_LABELS[statusForm.partnership_status]}`);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const generateContract = async () => {
    if (!window.confirm("Générer/regénérer le contrat de partenariat ?\n\nAttention : si un contrat est déjà accepté, il devra être re-accepté.")) return;
    try {
      await api.post(`/agencies/${id}/partnership-contract`);
      toast.success("Contrat généré — email envoyé à l'agence");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const downloadContract = async () => {
    const token = localStorage.getItem("mbk_token");
    const res = await fetch(`${API_BASE}/agencies/${id}/partnership-contract.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Contrat introuvable");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `contrat-partenariat-${agency.name}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!agency) return <div className="text-center text-gray-400 py-24">Chargement…</div>;
  const docs = agency.partnership_documents || {};
  const contract = agency.partnership_contract || {};

  return (
    <div data-testid="admin-agency-detail">
      <button onClick={() => navigate("/admin/agencies")} className="text-sm text-gray-500 hover:text-[#0a1e3f] inline-flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" /> Retour aux agences</button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0a1e3f]">{agency.name}</h1>
          <p className="text-sm text-gray-500 mt-1">{agency.city}, {agency.country} · {agency.representative_first_name} {agency.representative_last_name} · {agency.email}</p>
        </div>
        <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#0a1e3f] text-white" data-testid="admin-ag-ps">{PS_LABELS[agency.partnership_status] || agency.partnership_status}</span>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Documents */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="flex items-center gap-2 mb-4"><FileText className="h-5 w-5 text-[#0e7a3f]" /><div className="font-display text-lg font-black text-[#0a1e3f]">Documents soumis</div></div>
            {(agency.required_documents || []).length === 0 ? <div className="text-sm text-gray-500">Aucun document requis pour cette agence.</div> :
              <div className="space-y-3">
                {(agency.required_documents || []).map((d) => {
                  const info = docs[d] || {};
                  const status = info.status || "not_submitted";
                  const meta = STATUS_META[status];
                  const url = info.url ? (info.url.startsWith("http") ? info.url : `${API_BASE.replace(/\/api$/, "")}${info.url}`) : "";
                  return (
                    <div key={d} className="border border-gray-100 rounded-xl p-4" data-testid={`admin-doc-${d}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-[#0a1e3f]">{DOC_LABELS[d]}</div>
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${meta.cls}`}>{meta.label}</span>
                      </div>
                      {info.note && <div className="text-xs text-orange-800 bg-orange-50 rounded-lg p-2 mt-2">Note : {info.note}</div>}
                      {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#0a1e3f] hover:text-[#0e7a3f] underline mt-2">Voir document</a>
                      )}
                      {["submitted", "in_review"].includes(status) && (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          <button data-testid={`doc-validate-${d}`} onClick={() => reviewDoc(d, "validate")} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#0e7a3f] text-white text-xs font-bold"><CheckCircle2 className="h-3 w-3" /> Valider</button>
                          <button data-testid={`doc-replace-${d}`} onClick={() => reviewDoc(d, "request_replacement")} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-orange-500 text-white text-xs font-bold"><RefreshCw className="h-3 w-3" /> Remplacer</button>
                          <button data-testid={`doc-reject-${d}`} onClick={() => reviewDoc(d, "reject")} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-red-600 text-white text-xs font-bold"><XCircle className="h-3 w-3" /> Refuser</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            }
          </div>

          {/* Required docs config */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="font-display text-lg font-black text-[#0a1e3f] mb-3">Documents requis pour cette agence</div>
            <p className="text-xs text-gray-500 mb-4">Cochez les documents que l'agence doit fournir. Vous pouvez adapter selon le pays et l'activité.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {ALL_DOC_TYPES.map((d) => (
                <label key={d} className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-100 hover:border-[#0e7a3f] cursor-pointer">
                  <Checkbox data-testid={`req-${d}`} checked={required.includes(d)} onCheckedChange={(v) => setRequired((r) => v ? [...r, d] : r.filter((x) => x !== d))} />
                  <span className="text-sm">{DOC_LABELS[d]}</span>
                </label>
              ))}
            </div>
            <Button onClick={saveRequired} data-testid="save-required-docs" className="mt-4 mbk-btn-primary text-white h-10 rounded-full px-5">Enregistrer les documents requis</Button>
          </div>

          {/* Contract */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="flex items-center gap-2 mb-4"><FileSignature className="h-5 w-5 text-[#0e7a3f]" /><div className="font-display text-lg font-black text-[#0a1e3f]">Contrat de partenariat</div></div>
            {!contract.contract_upload_id ? (
              <div>
                <p className="text-sm text-gray-500 mb-4">Aucun contrat généré pour cette agence.</p>
                <Button onClick={generateContract} data-testid="gen-contract" className="mbk-btn-green text-white h-11 rounded-full px-5"><FileSignature className="h-4 w-4 mr-2" /> Générer le contrat</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm">Généré le : <b>{new Date(contract.generated_at).toLocaleString("fr-FR")}</b></div>
                <div className="text-sm">Accepté par l'agence : <b>{contract.agency_accepted_at ? `${contract.agency_signature_name} le ${new Date(contract.agency_accepted_at).toLocaleString("fr-FR")}` : "En attente"}</b></div>
                <div className="text-sm">Validé par MOBIKAR : <b>{contract.admin_validated_at ? new Date(contract.admin_validated_at).toLocaleString("fr-FR") : "En attente"}</b></div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={downloadContract} data-testid="dl-contract" className="mbk-btn-primary text-white h-10 rounded-full px-4 gap-2"><Download className="h-4 w-4" /> Télécharger</Button>
                  <Button onClick={generateContract} variant="outline" className="h-10 rounded-full px-4">Régénérer</Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Commission */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="font-display text-lg font-black text-[#0a1e3f] mb-3">Commission MOBIKAR</div>
            <div className="flex gap-2">
              <Input data-testid="commission-input" type="number" step="0.5" min="0" max="100" value={commission} onChange={(e) => setCommission(e.target.value)} className="h-11" />
              <Button onClick={saveCommission} data-testid="save-commission" className="mbk-btn-green text-white h-11 rounded-full px-5">Enregistrer</Button>
            </div>
            <div className="text-xs text-gray-500 mt-2">% prélevé sur chaque réservation payée via la plateforme.</div>
          </div>

          {/* Partnership status transitions */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="font-display text-lg font-black text-[#0a1e3f] mb-3">Statut du partenariat</div>
            <div className="space-y-3">
              <Select value={statusForm.partnership_status} onValueChange={(v) => setStatusForm((f) => ({ ...f, partnership_status: v }))}>
                <SelectTrigger data-testid="ps-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea data-testid="ps-note" rows={2} placeholder="Note interne (optionnel)" value={statusForm.note} onChange={(e) => setStatusForm((f) => ({ ...f, note: e.target.value }))} />
              <Button onClick={transitionStatus} data-testid="ps-save" className="mbk-btn-primary text-white h-11 w-full rounded-full">Appliquer le nouveau statut</Button>
            </div>
            <div className="text-xs text-gray-500 mt-3">Passer à <b>« Validée »</b> activera l'agence et lui permettra de gérer ses véhicules.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
